import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EnvironmentImportSummary from './EnvironmentImportSummary.js';
import EnvironmentImportPrevalidation from './EnvironmentImportPrevalidation.js';
import EnvironmentImportTargets from './EnvironmentImportTargets.js';
import EnvironmentImportDryRunReport from './EnvironmentImportDryRunReport.js';
import EnvironmentImportExecutionReport from './EnvironmentImportExecutionReport.js';
import { inspectSnapshot, prevalidateSnapshotLocally } from './environmentImportValidation.js';
import { checkEnvironmentImport, executeEnvironmentImport } from './environmentImportApi.js';
import { getGroups } from '../../../api/index.js';

const steps = ['Charger le snapshot', 'Récapitulatif', 'Prévalidation', 'Providers cibles', 'Pré-check', 'Import'];

const normalizeProviders = (payload) => {
  const rawValues = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.groups)
    ? payload.groups
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return [...new Set(
    rawValues
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.provider || item?.name || item?.value || item?.label || item?.group || '';
      })
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const EnvironmentImport = () => {
  const fileInputRef = useRef(null);
  const [snapshot, setSnapshot] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [readError, setReadError] = useState('');
  const [readWarnings, setReadWarnings] = useState([]);
  const [prevalidation, setPrevalidation] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [providerOptions, setProviderOptions] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [targetsByCompetition, setTargetsByCompetition] = useState({});
  const [gameFactStrategy, setGameFactStrategy] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadProviders = async () => {
      setProvidersLoading(true);
      try {
        const response = await getGroups();
        if (mounted) setProviderOptions(normalizeProviders(response?.data));
      } catch {
        if (mounted) setProviderOptions([]);
      } finally {
        if (mounted) setProvidersLoading(false);
      }
    };

    loadProviders();
    return () => { mounted = false; };
  }, []);

  const targetCompetitionIds = snapshot?.data?.competitions?.map((competition) => String(competition?._id || '')).filter(Boolean) || [];
  const allTargetsConfigured = targetCompetitionIds.length > 0 && targetCompetitionIds.every((competitionId) => (targetsByCompetition?.[competitionId] || []).length > 0);

  const activeStep = !snapshot
    ? 0
    : !prevalidation
    ? 1
    : !prevalidation.valid
    ? 2
    : importResult
    ? 5
    : dryRunResult
    ? 4
    : 3;

  const resetSnapshot = () => {
    setSnapshot(null);
    setFileInfo(null);
    setReadError('');
    setReadWarnings([]);
    setPrevalidation(null);
    setTargetsByCompetition({});
    setGameFactStrategy('');
    setCheckError('');
    setDryRunResult(null);
    setImportError('');
    setImportResult(null);
    setConfirmImportOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const readFile = async (file) => {
    if (!file) return;

    setReadError('');
    setReadWarnings([]);
    setPrevalidation(null);
    setTargetsByCompetition({});
    setGameFactStrategy('');
    setCheckError('');
    setDryRunResult(null);
    setImportError('');
    setImportResult(null);
    setConfirmImportOpen(false);

    if (!file.name.toLowerCase().endsWith('.json')) {
      setReadError('Sélectionne un fichier JSON de snapshot environnement.');
      return;
    }

    try {
      const content = await file.text();
      const parsed = JSON.parse(content);
      const inspection = inspectSnapshot(parsed);

      if (inspection.errors.length > 0) {
        setSnapshot(null);
        setFileInfo(null);
        setReadError(inspection.errors.map((error) => error.message).join(' '));
        setReadWarnings(inspection.warnings);
        return;
      }

      setSnapshot(parsed);
      setFileInfo({ name: file.name, size: file.size });
      setReadWarnings(inspection.warnings);
    } catch (error) {
      setSnapshot(null);
      setFileInfo(null);
      setReadError(`Impossible de lire le snapshot : ${error?.message || 'JSON invalide'}`);
    }
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];
    readFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    readFile(file);
  };

  const handlePrevalidate = () => {
    if (!snapshot) return;
    const result = prevalidateSnapshotLocally(snapshot);
    setPrevalidation(result);
    setCheckError('');
    setDryRunResult(null);
    setImportError('');
    setImportResult(null);

    if (result.valid) {
      const initialTargets = {};
      (snapshot?.data?.competitions || []).forEach((competition) => {
        initialTargets[String(competition?._id)] = targetsByCompetition?.[String(competition?._id)] || [];
      });
      setTargetsByCompetition(initialTargets);
    }
  };

  const handleTargetsChange = (nextTargets) => {
    setTargetsByCompetition(nextTargets);
    setDryRunResult(null);
    setCheckError('');
    setImportError('');
    setImportResult(null);
  };

  const handleGameFactStrategyChange = (strategy) => {
    setGameFactStrategy(strategy);
    setDryRunResult(null);
    setCheckError('');
    setImportError('');
    setImportResult(null);
  };

  const handleRunCheck = async () => {
    if (!snapshot || !prevalidation?.valid || !allTargetsConfigured || !gameFactStrategy) return;

    const targets = targetCompetitionIds.map((sourceCompetitionId) => ({
      sourceCompetitionId,
      targetProviders: targetsByCompetition[sourceCompetitionId] || [],
    }));

    setCheckLoading(true);
    setCheckError('');
    setDryRunResult(null);
    setImportError('');
    setImportResult(null);

    try {
      const response = await checkEnvironmentImport(snapshot, targets, gameFactStrategy);
      setDryRunResult(response?.data || null);
    } catch (error) {
      setCheckError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Impossible de pré-checker l'import."
      );
    } finally {
      setCheckLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!snapshot || !dryRunResult?.valid || !dryRunResult?.planFingerprint) return;

    const targets = targetCompetitionIds.map((sourceCompetitionId) => ({
      sourceCompetitionId,
      targetProviders: targetsByCompetition[sourceCompetitionId] || [],
    }));

    setImportLoading(true);
    setImportError('');
    setImportResult(null);

    try {
      const response = await executeEnvironmentImport(
        snapshot,
        targets,
        gameFactStrategy,
        dryRunResult.planFingerprint
      );
      setImportResult(response?.data || null);
      setConfirmImportOpen(false);
    } catch (error) {
      const code = error?.response?.data?.code;
      if (code === 'IMPORT_PLAN_CHANGED' || code === 'IMPORT_PRECHECK_BLOCKED') {
        setDryRunResult(null);
        setImportError(
          error?.response?.data?.message ||
            "La destination a changé. Relance le pré-check avant d'importer."
        );
      } else {
        setImportError(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Impossible d'exécuter l'import."
        );
      }
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Import environnement
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Charge un snapshot exporté, contrôle son contenu puis prépare sa validation avant importation.
          </Typography>
        </Box>

        <Alert severity="info">
          Le pré-check reste entièrement non destructif. L'écriture réelle n'est autorisée qu'après validation du rapport et un nouveau contrôle serveur du même plan.
        </Alert>

        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label} completed={activeStep > steps.indexOf(label)}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">1. Snapshot JSON</Typography>
                <Typography variant="body2" color="text.secondary">
                  Glisse le fichier ici ou sélectionne-le depuis ton poste.
                </Typography>
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={handleInputChange}
              />

              <Paper
                variant="outlined"
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  bgcolor: dragActive ? 'action.hover' : 'background.paper',
                }}
              >
                <UploadFileOutlinedIcon sx={{ fontSize: 42, mb: 1 }} />
                <Typography fontWeight={600}>
                  {fileInfo ? fileInfo.name : 'Sélectionner un snapshot environnement'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fileInfo ? formatBytes(fileInfo.size) : 'Fichier .json'}
                </Typography>
              </Paper>

              {fileInfo && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={resetSnapshot}
                  >
                    Retirer le fichier
                  </Button>
                </Box>
              )}

              {readError && <Alert severity="error">{readError}</Alert>}
              {readWarnings.map((warning, index) => (
                <Alert severity="warning" key={`${warning.type}-${index}`}>
                  {warning.message}
                </Alert>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {snapshot && (
          <Card>
            <CardContent>
              <EnvironmentImportSummary snapshot={snapshot} />
            </CardContent>
          </Card>
        )}

        {snapshot && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">3. Prévalidation avant import</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vérifie les identités et les références internes du snapshot avant de préparer sa destination.
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Button
                    variant="contained"
                    startIcon={<FactCheckOutlinedIcon />}
                    onClick={handlePrevalidate}
                  >
                    Prévalider le snapshot
                  </Button>
                </Box>

                <EnvironmentImportPrevalidation result={prevalidation} />
              </Stack>
            </CardContent>
          </Card>
        )}

        {prevalidation?.valid && (
          <EnvironmentImportTargets
            snapshot={snapshot}
            providerOptions={providerOptions}
            providersLoading={providersLoading}
            targetsByCompetition={targetsByCompetition}
            onTargetsChange={handleTargetsChange}
            onRunCheck={handleRunCheck}
            checkLoading={checkLoading}
            gameFactStrategy={gameFactStrategy}
            onGameFactStrategyChange={handleGameFactStrategyChange}
          />
        )}

        {checkError && <Alert severity="error">{checkError}</Alert>}

        <EnvironmentImportDryRunReport result={dryRunResult} />

        {dryRunResult?.valid && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">6. Valider et importer</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Le backend refera le pré-check immédiatement avant les écritures et vérifiera que son fingerprint correspond exactement au rapport affiché.
                  </Typography>
                </Box>

                <Alert severity="warning">
                  L'import peut créer et modifier des documents. Les seules suppressions autorisées concernent les faits de jeu des Matchs déjà existants lorsque la stratégie « Remplacer » a été choisie. Les relations hors scope restent conservées.
                </Alert>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<CloudUploadOutlinedIcon />}
                    disabled={importLoading || Boolean(importResult)}
                    onClick={() => setConfirmImportOpen(true)}
                  >
                    {importLoading ? 'Import en cours...' : 'Importer les données validées'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {importError && <Alert severity="error">{importError}</Alert>}

        <EnvironmentImportExecutionReport result={importResult} />

        <Dialog open={confirmImportOpen} onClose={() => !importLoading && setConfirmImportOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirmer l'import environnement</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                Tu vas exécuter exactement le plan validé par le pré-check courant.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Typography variant="body2"><strong>{dryRunResult?.summary?.create || 0}</strong> création(s)</Typography>
                <Typography variant="body2"><strong>{dryRunResult?.summary?.update || 0}</strong> modification(s)</Typography>
                <Typography variant="body2"><strong>{dryRunResult?.summary?.unchanged || 0}</strong> déjà à jour</Typography>
              </Stack>
              <Alert severity={gameFactStrategy === 'replace' ? 'warning' : 'info'}>
                <Typography variant="body2" fontWeight={600}>
                  Faits de jeu : {gameFactStrategy === 'replace' ? 'remplacement sur les Matchs existants' : 'aucune écriture sur les Matchs existants'}
                </Typography>
                {gameFactStrategy === 'replace' ? (
                  <Typography variant="body2">
                    {dryRunResult?.gameFacts?.existingTargetGameFactsToDelete || 0} fait(s) existant(s) seront remplacés et {dryRunResult?.gameFacts?.snapshotGameFactsToCreate || 0} fait(s) du snapshot seront créés.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    {dryRunResult?.gameFacts?.snapshotGameFactsSkipped || 0} fait(s) du snapshot seront ignorés sur les Matchs déjà présents. Les faits des Matchs nouvellement créés seront tout de même créés.
                  </Typography>
                )}
              </Alert>
              <Alert severity="info">
                Ordre d'écriture : Competition → Team → Player → Match → GameFact V1. Les ObjectId source servent uniquement à reconstruire les références du snapshot ; les créations reçoivent de nouveaux ObjectId cible.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmImportOpen(false)} disabled={importLoading}>Annuler</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleExecuteImport}
              disabled={importLoading}
              startIcon={<CloudUploadOutlinedIcon />}
            >
              {importLoading ? 'Import en cours...' : 'Confirmer et importer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default EnvironmentImport;
